import React from "react";
import DataSkeleton from "../../StayUpToDateComponent/UpToDateComponents/DataSkeleton";
import StayUpToDateSkeleton from "../../../Screens/DashboardView/StayUpToDate/StayUpToDateComponents/StayUpToDateSkeleton";
import { useDesign } from "../../../context/DesignContext";
import { useWebsiteTemplateLoaders } from "../../../Screens/DashboardView/MasterTemplateEditor/TemplateFeedDesignBoundary";
import { WebsiteLibLandingSkeleton } from "../../../Screens/DashboardView/MasterTemplateEditor/WebsiteLibFeedSkeleton";

export interface DesignTokens {
  fontFamily: string;
  primaryColor: string;
  secondaryColor: string;
  tertiaryColor: string;
  templateName: string;
}

interface LandingPreviewSkeletonProps {
  /** When true, shows the component/detail view skeleton (DataSkeleton); otherwise the landing list skeleton (StayUpToDateSkeleton). */
  isDetailView: boolean;
  designTokens?: DesignTokens;
  /** Number of skeleton items for the detail view. Default 8. */
  arrayLength?: number; from?:any, blockArray?:any
  isSearch?: boolean;
  isDashboard?: boolean;
  /**
   * Force website template bones (Home/LiB). When omitted, auto-enables under
   * {@link TemplateFeedDesignBoundary}.
   */
  variant?: "classic" | "website";
  device?: "desktop" | "tablet" | "mobile";
  /** Contact Me card: form bones instead of a carousel strip. */
  contact?: boolean;
}

/**
 * Renders the appropriate skeleton for landing/preview: DataSkeleton for a single component (detail) view,
 * StayUpToDateSkeleton for the main landing page. Use wherever loading state differs by route (e.g. param?.id).
 */
const LandingPreviewSkeleton = ({
  isDetailView,
  isDashboard,
  designTokens: designTokensProps,
  arrayLength = 8,
  blockArray = 3,
  isSearch,
  from,
  variant,
  device = "desktop",
  contact = false,
}: LandingPreviewSkeletonProps) => {
  const { templateName, primaryColor, secondaryColor, tertiaryColor, fontFamily } =
    useDesign();
  const inWebsiteSurface = useWebsiteTemplateLoaders();
  const useWebsiteBones =
    variant === "website" || (variant !== "classic" && inWebsiteSurface);

  const isDashboardEmbed = from === "embed-search" || isDashboard;
  const designTokens = isDashboardEmbed
    ? { templateName: "default" }
    : {
        templateName: designTokensProps?.templateName ?? templateName ?? "",
        primaryColor: designTokensProps?.primaryColor ?? primaryColor ?? "",
        secondaryColor:
          designTokensProps?.secondaryColor ?? secondaryColor ?? "",
        tertiaryColor: designTokensProps?.tertiaryColor ?? tertiaryColor ?? "",
        fontFamily: designTokensProps?.fontFamily ?? fontFamily ?? "",
      };

  if (useWebsiteBones) {
    if (isSearch) {
      return <p>Search</p>;
    }
    return (
      <WebsiteLibLandingSkeleton
        isDetailView={isDetailView}
        blockArray={blockArray}
        device={device}
        contact={contact}
      />
    );
  }

  if (isDetailView) {
    return (
      <DataSkeleton
        arrayLength={arrayLength}
        designTokens={designTokens}
        from={from}
        individualGrid
      />
    );
  }
  if (isSearch) {
    return <p>Search</p>;
  }
  return (
    <StayUpToDateSkeleton designTokens={designTokens} blockArray={blockArray} isDashboard={isDashboard}/>
  );
};

export default LandingPreviewSkeleton;
